import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  danger?: boolean;
  isLight: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  danger,
  isLight,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                danger
                  ? isLight
                    ? 'bg-red-50 text-red-600'
                    : 'bg-red-500/15 text-red-300'
                  : isLight
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-indigo-500/15 text-indigo-300'
              }`}
            >
              <AlertTriangle size={18} />
            </div>
            <div>
              <h3 className={`text-base font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h3>
              <p className={`text-sm mt-1.5 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{description}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className={`p-1.5 rounded-lg ${isLight ? 'hover:bg-neutral-100 text-neutral-400' : 'hover:bg-neutral-800 text-neutral-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div
          className={`px-5 py-3.5 flex justify-end gap-2 border-t ${
            isLight ? 'border-neutral-100 bg-neutral-50/80' : 'border-neutral-800 bg-neutral-900/80'
          }`}
        >
          <button
            type="button"
            onClick={onCancel}
            className={`h-9 px-3.5 text-sm font-medium rounded-xl border ${
              isLight
                ? 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                : 'border-neutral-700 bg-neutral-900 text-neutral-200 hover:bg-neutral-800'
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`h-9 px-3.5 text-sm font-medium rounded-xl text-white ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
